import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Stars as StarsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Visibility as VisibilityIcon,
  PlayArrow as PlayArrowIcon,
  DeleteSweep as DeleteSweepIcon,
  Schedule as ScheduleIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { loyaltyProgramService } from '../services/loyaltyProgramService';
import { LoyaltyProgram, LoyaltyProgramFormData } from '../types/loyaltyProgram';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { useLoyaltyPermissions } from '../hooks/useLoyaltyPermissions';

const LoyaltyPointsPage: React.FC = () => {
  const {
    canSetupLoyalty,
    canManualProcess,
    canDeleteAll,
    canEdit,
    canAutoSchedule,
    canDeactivate,
    canDelete
  } = useLoyaltyPermissions();
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<LoyaltyProgram | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // حالات الجدولة التلقائية
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [selectedProgramForSchedule, setSelectedProgramForSchedule] = useState<string | null>(null);
  const [autoScheduleEnabled, setAutoScheduleEnabled] = useState(false);
  const [scheduleFrequency, setScheduleFrequency] = useState<'daily' | 'weekly'>('daily');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduleDay, setScheduleDay] = useState<number>(1);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  
  const [formData, setFormData] = useState<LoyaltyProgramFormData>({
    name: '',
    description: '',
    salesInvoicePointsAfterDays: 0,
    returnInvoicePointsAfterDays: 0,
    pointsPerRiyal: 1,
    riyalPerPoint: 0.1,
    isActive: true,
    minimumInvoiceAmount: 0,
    customerCategories: ['regular', 'premium', 'vip'],
    pointsExpiry: {
      enabled: false,
      expiryDays: 365
    }
  });

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await loyaltyProgramService.getLoyaltyPrograms();
      setPrograms(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء جلب برامج الولاء');
      console.error('Error fetching programs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (program?: LoyaltyProgram) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        name: program.name,
        description: program.description || '',
        salesInvoicePointsAfterDays: program.salesInvoicePointsAfterDays || 0,
        returnInvoicePointsAfterDays: program.returnInvoicePointsAfterDays || 0,
        pointsPerRiyal: program.pointsPerRiyal || 1,
        riyalPerPoint: program.riyalPerPoint || 0.1,
        isActive: program.isActive !== undefined ? program.isActive : true,
        minimumInvoiceAmount: program.minimumInvoiceAmount || 0,
        maximumPointsPerInvoice: program.maximumPointsPerInvoice || undefined,
        customerCategories: program.customerCategories || ['regular', 'premium', 'vip'],
        pointsExpiry: program.pointsExpiry || {
          enabled: false,
          expiryDays: 365
        }
      });
    } else {
      setEditingProgram(null);
      setFormData({
        name: '',
        description: '',
        salesInvoicePointsAfterDays: 0,
        returnInvoicePointsAfterDays: 0,
        pointsPerRiyal: 1,
        riyalPerPoint: 0.1,
        isActive: true,
        minimumInvoiceAmount: 0,
        customerCategories: ['regular', 'premium', 'vip'],
        pointsExpiry: {
          enabled: false,
          expiryDays: 365
        }
      });
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingProgram(null);
  };

  const handleSubmit = async () => {
    try {
      console.log('📤 Sending data:', formData);
      console.log('🔄 Editing program:', editingProgram);
      
      if (editingProgram) {
        const response = await loyaltyProgramService.updateLoyaltyProgram(editingProgram._id, formData);
        console.log('✅ Update response:', response);
      } else {
        const response = await loyaltyProgramService.createLoyaltyProgram(formData);
        console.log('✅ Create response:', response);
      }
      handleCloseModal();
      fetchPrograms();
    } catch (err: any) {
      console.error('❌ Error saving program:', err);
      console.error('📋 Error response:', err.response?.data);
      console.error('📊 Error status:', err.response?.status);
      console.error('📝 Error message:', err.response?.data?.message);
      console.error('🔍 Error details:', err.response?.data?.details);
      console.error('⚠️ Error errors:', err.response?.data?.errors);
      setError(err.response?.data?.message || err.response?.data?.details || 'حدث خطأ أثناء حفظ البرنامج');
    }
  };

  const handleToggleStatus = async (id: string) => {
    try {
      await loyaltyProgramService.toggleLoyaltyProgramStatus(id);
      fetchPrograms();
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تغيير حالة البرنامج');
      console.error('Error toggling status:', err);
    }
  };

  const handleDeleteClick = (id: string) => {
    setProgramToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (programToDelete) {
      try {
        await loyaltyProgramService.deleteLoyaltyProgram(programToDelete);
        setDeleteDialogOpen(false);
        setProgramToDelete(null);
        fetchPrograms();
      } catch (err: any) {
        setError(err.response?.data?.message || 'حدث خطأ أثناء حذف البرنامج');
        console.error('Error deleting program:', err);
      }
    }
  };

  const handleProcessPoints = async () => {
    try {
      setProcessing(true);
      setProcessResult(null);
      setError(null);

      // معالجة النقاط من الفواتير
      const invoiceResponse = await loyaltyProgramService.processLoyaltyPoints();
      
      // معالجة النقاط المنتهية
      const expiredResponse = await loyaltyProgramService.processExpiredPoints();
      
      let resultMessage = '';
      
      if (invoiceResponse.success) {
        resultMessage += `✅ معالجة الفواتير: ${invoiceResponse.message}\n`;
        resultMessage += `📊 تمت معالجة ${invoiceResponse.processed} فاتورة\n`;
        resultMessage += `⭐ إجمالي النقاط المضافة: ${invoiceResponse.totalPoints}\n\n`;
      }
      
      if (expiredResponse.success) {
        if (expiredResponse.processed > 0) {
          resultMessage += `🕐 معالجة النقاط المنتهية: ${expiredResponse.message}\n`;
          resultMessage += `👥 تمت معالجة ${expiredResponse.processed} عميل\n`;
          resultMessage += `⏰ إجمالي النقاط المنتهية: ${expiredResponse.totalExpiredPoints}`;
        } else {
          resultMessage += `✅ لا توجد نقاط منتهية الصلاحية`;
        }
      }
      
      setProcessResult(resultMessage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء معالجة النقاط');
      console.error('Error processing points:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteAllPoints = async () => {
    try {
      setDeleting(true);
      setProcessResult(null);
      setError(null);

      const response = await loyaltyProgramService.deleteAllLoyaltyPoints();
      
      if (response.success) {
        setProcessResult(`✅ ${response.message}`);
        setDeleteConfirmOpen(false);
      } else {
        setError(response.message || 'فشلت عملية الحذف');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء حذف النقاط');
      console.error('Error deleting points:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: ar });
  };

  // فتح نافذة الجدولة
  const handleOpenScheduleDialog = async (programId: string) => {
    setSelectedProgramForSchedule(programId);
    
    // جلب الإعدادات الحالية
    try {
      const response = await loyaltyProgramService.getScheduleSettings(programId);
      if (response.success && response.data) {
        setAutoScheduleEnabled(response.data.enabled || false);
        setScheduleFrequency(response.data.frequency || 'daily');
        setScheduleTime(response.data.time || '09:00');
        setScheduleDay(response.data.day || 1);
      }
    } catch (err) {
      console.error('Error fetching schedule settings:', err);
    }
    
    setScheduleDialogOpen(true);
  };

  // إغلاق نافذة الجدولة
  const handleCloseScheduleDialog = () => {
    setScheduleDialogOpen(false);
    setSelectedProgramForSchedule(null);
    setAutoScheduleEnabled(false);
    setScheduleFrequency('daily');
    setScheduleTime('09:00');
    setScheduleDay(1);
  };

  // حفظ إعدادات الجدولة
  const handleSaveSchedule = async () => {
    if (!selectedProgramForSchedule) return;
    
    try {
      setScheduleSaving(true);
      
      const scheduleSettings = {
        enabled: autoScheduleEnabled,
        frequency: scheduleFrequency,
        time: scheduleTime,
        day: scheduleFrequency === 'weekly' ? scheduleDay : undefined
      };

      const response = await loyaltyProgramService.updateScheduleSettings(
        selectedProgramForSchedule,
        scheduleSettings
      );
      
      if (response.success) {
        setProcessResult(`✅ تم حفظ إعدادات الجدولة التلقائية بنجاح`);
        handleCloseScheduleDialog();
        fetchPrograms(); // تحديث قائمة البرامج
      }
    } catch (err: any) {
      console.error('Error saving schedule:', err);
      setError(err.response?.data?.message || 'حدث خطأ أثناء حفظ إعدادات الجدولة');
    } finally {
      setScheduleSaving(false);
    }
  };

  // أسماء الأيام
  const weekDays = [
    { value: 1, label: 'الاثنين' },
    { value: 2, label: 'الثلاثاء' },
    { value: 3, label: 'الأربعاء' },
    { value: 4, label: 'الخميس' },
    { value: 5, label: 'الجمعة' },
    { value: 6, label: 'السبت' },
    { value: 0, label: 'الأحد' }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Card */}
      <Card sx={{
        mb: 3,
        background: 'linear-gradient(120deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StarsIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                نقاط الولاء
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                إدارة برامج الولاء ونقاط العملاء
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canDeleteAll() && (
              <Button
                variant="outlined"
                color="error"
                startIcon={deleting ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleting}
                sx={{
                  bgcolor: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(244, 67, 54, 0.1)'
                  }
                }}
              >
                {deleting ? 'جاري الحذف...' : 'حذف جميع النقاط'}
              </Button>
            )}
            {canManualProcess() && (
              <Button
                variant="outlined"
                startIcon={processing ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                onClick={handleProcessPoints}
                disabled={processing || programs.length === 0}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  borderColor: '#667eea',
                  '&:hover': {
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    borderColor: '#667eea'
                  }
                }}
              >
                {processing ? 'جاري المعالجة...' : 'معالجة النقاط يدوياً'}
              </Button>
            )}
            {canSetupLoyalty() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenModal()}
                sx={{
                  bgcolor: 'white',
                  color: '#667eea',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' }
                }}
              >
                إعداد برنامج ولاء
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {processResult && (
        <Alert
          severity="success"
          sx={{ mb: 2, whiteSpace: 'pre-line' }}
          onClose={() => setProcessResult(null)}
        >
          {processResult}
        </Alert>
      )}

      {processResult && (
        <Alert
          severity="success"
          sx={{ mb: 2, whiteSpace: 'pre-line' }}
          onClose={() => setProcessResult(null)}
        >
          {processResult}
        </Alert>
      )}

      {/* Programs Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            البرامج المحفوظة
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>اسم البرنامج</TableCell>
                  <TableCell align="center">أيام المبيعات</TableCell>
                  <TableCell align="center">أيام المرتجعات</TableCell>
                  <TableCell align="center">نقاط/ريال</TableCell>
                  <TableCell align="center">ريال/نقطة</TableCell>
                  <TableCell align="center">الحالة</TableCell>
                  <TableCell align="center">تاريخ الإنشاء</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {programs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        لا توجد برامج ولاء. قم بإنشاء برنامج جديد للبدء.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  programs.map((program) => (
                    <TableRow key={program._id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {program.name}
                        </Typography>
                        {program.description && (
                          <Typography variant="caption" color="text.secondary">
                            {program.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {program.salesInvoicePointsAfterDays} يوم
                      </TableCell>
                      <TableCell align="center">
                        {program.returnInvoicePointsAfterDays} يوم
                      </TableCell>
                      <TableCell align="center">{program.pointsPerRiyal}</TableCell>
                      <TableCell align="center">{program.riyalPerPoint}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={program.isActive ? 'نشط' : 'غير نشط'}
                          color={program.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        {formatDate(program.createdAt)}
                      </TableCell>
                      <TableCell align="center">
                        {canEdit() && (
                          <Tooltip title="تعديل">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenModal(program)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canAutoSchedule() && (
                          <Tooltip title="الجدولة التلقائية">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleOpenScheduleDialog(program._id)}
                            >
                              <ScheduleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDeactivate() && (
                          <Tooltip title={program.isActive ? 'إلغاء التفعيل' : 'تفعيل'}>
                            <IconButton
                              size="small"
                              color={program.isActive ? 'warning' : 'success'}
                              onClick={() => handleToggleStatus(program._id)}
                            >
                              {program.isActive ? (
                                <ToggleOffIcon fontSize="small" />
                              ) : (
                                <ToggleOnIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                        {canDelete() && (
                          <Tooltip title="حذف">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(program._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProgram ? 'تعديل برنامج الولاء' : 'إعداد برنامج ولاء جديد'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="اسم البرنامج"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="الوصف"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="إضافة النقاط لفواتير المبيعات بعد (أيام)"
                value={formData.salesInvoicePointsAfterDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    salesInvoicePointsAfterDays: parseInt(e.target.value) || 0
                  })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="إضافة النقاط لفواتير المرتجعات بعد (أيام)"
                value={formData.returnInvoicePointsAfterDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    returnInvoicePointsAfterDays: parseInt(e.target.value) || 0
                  })
                }
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="عدد النقاط المكتسبة مقابل الريال"
                value={formData.pointsPerRiyal}
                onChange={(e) =>
                  setFormData({ ...formData, pointsPerRiyal: parseFloat(e.target.value) || 0 })
                }
                required
                inputProps={{ step: '0.1', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="قيمة استبدال النقاط بالريال"
                value={formData.riyalPerPoint}
                onChange={(e) =>
                  setFormData({ ...formData, riyalPerPoint: parseFloat(e.target.value) || 0 })
                }
                required
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="الحد الأدنى لقيمة الفاتورة"
                value={formData.minimumInvoiceAmount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    minimumInvoiceAmount: parseFloat(e.target.value) || 0
                  })
                }
                inputProps={{ step: '0.01', min: '0' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="الحد الأقصى للنقاط في الفاتورة (اختياري)"
                value={formData.maximumPointsPerInvoice || ''}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    maximumPointsPerInvoice: e.target.value ? parseInt(e.target.value) : undefined
                  })
                }
                inputProps={{ min: '0' }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.pointsExpiry?.enabled || false}
                    onChange={(e) => setFormData({
                      ...formData,
                      pointsExpiry: {
                        ...formData.pointsExpiry,
                        enabled: e.target.checked,
                        expiryDays: formData.pointsExpiry?.expiryDays || 365
                      }
                    })}
                  />
                }
                label="تفعيل انتهاء صلاحية النقاط"
              />
            </Grid>
            {formData.pointsExpiry?.enabled && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="عدد أيام صلاحية النقاط"
                  value={formData.pointsExpiry?.expiryDays || 365}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      pointsExpiry: {
                        ...formData.pointsExpiry,
                        enabled: formData.pointsExpiry?.enabled || false,
                        expiryDays: parseInt(e.target.value) || 365
                      }
                    })
                  }
                  inputProps={{ min: '1' }}
                  helperText="عدد الأيام التي تنتهي بعدها صلاحية النقاط"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  />
                }
                label="تفعيل البرنامج"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>إلغاء</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingProgram ? 'تحديث' : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>تأكيد الحذف</DialogTitle>
        <DialogContent>
          <Typography>هل أنت متأكد من حذف هذا البرنامج؟ لا يمكن التراجع عن هذا الإجراء.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            حذف
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete All Points Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle sx={{ color: 'error.main' }}>⚠️ تحذير: حذف جميع النقاط</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            هل أنت متأكد من حذف <strong>جميع نقاط الولاء</strong> لجميع العملاء؟
          </Typography>
          <Typography variant="body2" color="error">
            ⚠️ هذا الإجراء سيحذف جميع السجلات ولا يمكن التراجع عنه!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>إلغاء</Button>
          <Button
            onClick={handleDeleteAllPoints}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteSweepIcon />}
          >
            {deleting ? 'جاري الحذف...' : 'نعم، احذف الكل'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* نافذة إعدادات الجدولة التلقائية */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={handleCloseScheduleDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <ScheduleIcon color="primary" />
            <Typography variant="h6">إعدادات الجدولة التلقائية</Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {/* تفعيل/إيقاف الجدولة */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoScheduleEnabled}
                  onChange={(e) => setAutoScheduleEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  تفعيل الجدولة التلقائية
                </Typography>
              }
              sx={{ mb: 3 }}
            />

            <Divider sx={{ mb: 3 }} />

            {/* خيارات الجدولة */}
            {autoScheduleEnabled && (
              <Grid container spacing={3}>
                {/* تكرار الجدولة */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>تكرار الجدولة</InputLabel>
                    <Select
                      value={scheduleFrequency}
                      label="تكرار الجدولة"
                      onChange={(e) => setScheduleFrequency(e.target.value as 'daily' | 'weekly')}
                    >
                      <MenuItem value="daily">كل يوم</MenuItem>
                      <MenuItem value="weekly">كل أسبوع</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* اختيار اليوم (للجدولة الأسبوعية فقط) */}
                {scheduleFrequency === 'weekly' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>اليوم</InputLabel>
                      <Select
                        value={scheduleDay}
                        label="اليوم"
                        onChange={(e) => setScheduleDay(Number(e.target.value))}
                      >
                        {weekDays.map((day) => (
                          <MenuItem key={day.value} value={day.value}>
                            {day.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* اختيار الساعة */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="time"
                    label="الساعة"
                    value={scheduleTime}
                    onChange={(e) => setScheduleTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      step: 300 // 5 دقائق
                    }}
                  />
                </Grid>

                {/* معلومات إضافية */}
                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    {scheduleFrequency === 'daily' ? (
                      <>
                        سيتم معالجة نقاط الولاء تلقائياً كل يوم في الساعة {scheduleTime}
                      </>
                    ) : (
                      <>
                        سيتم معالجة نقاط الولاء تلقائياً كل {weekDays.find(d => d.value === scheduleDay)?.label} في الساعة {scheduleTime}
                      </>
                    )}
                  </Alert>
                </Grid>
              </Grid>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseScheduleDialog}
            disabled={scheduleSaving}
          >
            إلغاء
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveSchedule}
            disabled={scheduleSaving}
            startIcon={scheduleSaving ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {scheduleSaving ? 'جاري الحفظ...' : 'حفظ'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LoyaltyPointsPage;